import {
  Component,
  computed,
  inject,
  input,
  LOCALE_ID,
  output,
  signal,
  OnInit,
  DestroyRef,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, switchMap, catchError, of } from 'rxjs';

import { FilterInput } from '../../../../../shared/ui/shared-filters/filter-input/filter-input';
import { FilterDateComponent } from '../../../../../shared/ui/shared-filters/filter-date-component/filter-date-component';
import { FilterSortingComponent } from '../../../../../shared/ui/shared-filters/filter-sorting-component/filter-sorting-component';
import { FilterSelectComponent } from '../../../../../shared/ui/shared-filters/filter-select-component/filter-select-component';

import { LookupService } from '../../../../../services/pharmacy/lookup.service';
import { LookupVTO } from '../../../../../pages/admin-dashboard/admin-companies/admin-companies.model';
import { RegionFilters } from '../../../../../pages/admin-dashboard/region-view-list/list-of-regions/list-of-regions.model';

@Component({
  selector: 'app-regions-list-filter',
  standalone: true,
  imports: [FilterInput, FilterDateComponent, FilterSortingComponent, FilterSelectComponent],
  templateUrl: './regions-list-filter.html',
  styleUrl: './regions-list-filter.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RegionsListFilter implements OnInit {
  private locale = inject(LOCALE_ID);
  private lookupService = inject(LookupService);
  private destroyRef = inject(DestroyRef);

  filters = input.required<RegionFilters>();

  applyFilters = output<Partial<RegionFilters>>();
  resetFilters = output<void>();

  quickSearch = signal<string>('');
  governorateId = signal<number | null>(null);
  cityId = signal<number | null>(null);
  createdOnFrom = signal<string | null>(null);
  createdOnTo = signal<string | null>(null);
  lastModifiedOnFrom = signal<string | null>(null);
  lastModifiedOnTo = signal<string | null>(null);

  createdDateInvalid = signal<boolean>(false);
  lastModifiedDateInvalid = signal<boolean>(false);

  governorates = signal<LookupVTO[]>([]);
  cities = signal<LookupVTO[]>([]);
  loadingCities = signal<boolean>(false);

  private governorateChange$ = new Subject<number[]>();

  isArabic = computed(() => this.locale.startsWith('ar'));

  readonly governorateOptions = computed(() =>
    this.governorates().map((g) => ({ id: g.id, titleAr: g.titleAr, titleEn: g.titleEn })),
  );

  readonly cityOptions = computed(() =>
    this.cities().map((c) => ({ id: c.id, titleAr: c.titleAr, titleEn: c.titleEn })),
  );

  sortOptions = computed(() => [
    { value: 'CREATION_DATE', labelEn: 'Creation Date', labelAr: 'تاريخ الإنشاء' },
    { value: 'LAST_MODIFICATION_DATE', labelEn: 'Last Modification', labelAr: 'تاريخ آخر تعديل' },
    { value: 'TITLE', labelEn: 'Title', labelAr: 'العنوان' },
  ]);

  ngOnInit(): void {
    this.lookupService
      .getGovernorates()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.governorates.set(res.list || []),
      });

    this.governorateChange$
      .pipe(
        switchMap((ids) => {
          this.loadingCities.set(true);
          return this.lookupService.getCities(ids).pipe(catchError(() => of({ list: [] })));
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((res) => {
        this.cities.set(res.list || []);
        this.loadingCities.set(false);
      });
  }

  onGovernorateChange(id: any) {
    this.governorateId.set(id ? Number(id) : null);
    this.cityId.set(null);
    this.cities.set([]);
    if (id) {
      this.governorateChange$.next([Number(id)]);
    }
  }

  onOrderByChange(value: string) {
    const current = this.filters();
    this.applyFilters.emit({ orderBy: value, orderDir: current.orderDir });
  }

  onToggleSortOrder() {
    const current = this.filters();
    this.applyFilters.emit({ orderDir: current.orderDir === 'ASC' ? 'DESC' : 'ASC' });
  }

  onResetFilters() {
    this.quickSearch.set('');
    this.governorateId.set(null);
    this.cityId.set(null);
    this.createdOnFrom.set(null);
    this.createdOnTo.set(null);
    this.lastModifiedOnFrom.set(null);
    this.lastModifiedOnTo.set(null);
    this.cities.set([]);
    this.resetFilters.emit();
  }

  onApplyFilters() {
    const createdInvalid = !!(
      this.createdOnFrom() &&
      this.createdOnTo() &&
      this.createdOnFrom()! > this.createdOnTo()!
    );
    const lastModInvalid = !!(
      this.lastModifiedOnFrom() &&
      this.lastModifiedOnTo() &&
      this.lastModifiedOnFrom()! > this.lastModifiedOnTo()!
    );

    this.createdDateInvalid.set(createdInvalid);
    this.lastModifiedDateInvalid.set(lastModInvalid);

    if (createdInvalid || lastModInvalid) return;

    this.applyFilters.emit({
      quickSearch: this.quickSearch() || undefined,
      governorateId: this.governorateId() || undefined,
      cityId: this.cityId() || undefined,
      createdOnFrom: this.createdOnFrom() ?? undefined,
      createdOnTo: this.createdOnTo() ?? undefined,
      lastModifiedOnFrom: this.lastModifiedOnFrom() ?? undefined,
      lastModifiedOnTo: this.lastModifiedOnTo() ?? undefined,
    });
  }
}
